import React, { useMemo, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import classNames from 'classnames';
import { v4 as UUID } from 'uuid';
import * as styles from './ExclusionForm.module.scss';
import validateReason, { errorMessages } from './validateReason';
import { ReorderItems, ReorderItem } from 'components/core/Reorder';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Icon,
  IconList,
  Text,
  VisuallyHidden,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import {
  deleteExclusionReason,
  createExclusionReason,
  reorderExclusionReasons,
} from 'query/review/ExclusionReasons';
import type { ExclusionReasons } from 'pages/StudyList/StudyListItem/ExclusionReason/ExclusionReason';

type ExclusionFormProps = {
  title: string;
  label: string;
  reasons: ExclusionReasons;
  setReasons: (reasons: ExclusionReasons) => void;
  isSubmitting?: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onApiError: (errors: string[] | null) => void;
  onComplete?: () => void;
};

type FormValues = {
  newReason: string;
  reasons: ExclusionReasons;
};

type APIError = {
  responseJSON: {
    errors: Record<'excluded_reasons', string[]>;
  };
};

const getApiErrorMessages = (error: unknown): string[] => {
  if (
    error &&
    typeof error === 'object' &&
    Object.keys(error).includes('responseJSON')
  ) {
    return (error as APIError).responseJSON.errors.excluded_reasons ?? [];
  }
  return [errorMessages.generic];
};

const ExclusionForm = ({
  title,
  label,
  reasons,
  setReasons,
  isSubmitting = false,
  setIsSubmitting,
  onApiError,
  onComplete,
}: ExclusionFormProps) => {
  const describedById = useMemo(() => UUID(), []);
  const [deletingReasons, setDeletingReasons] = useState<string[]>([]);
  const [screenReader, setScreenReader] = useState('');

  const {
    register,
    control,
    resetField,
    handleSubmit,
    formState,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      newReason: '',
      reasons: reasons,
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    keyName: 'uuid',
    name: 'reasons',
  });

  const onMove = async (from: number, to: number) => {
    move(from, to);
    const reorderedExclusionReasons = getValues('reasons');
    await reorderExclusionReasons({
      positions: reorderedExclusionReasons.map((f) => f.id),
      onApiError,
    });
    setReasons(reorderedExclusionReasons);
    onComplete?.();
  };

  const hasError = formState.errors.newReason?.message;

  const onAdd: SubmitHandler<FormValues> = async (data, event) => {
    event?.preventDefault();

    const newReason = data.newReason.trim();
    if (!newReason) return;

    setIsSubmitting(true);
    try {
      const response = await createExclusionReason({
        reason: newReason,
        onApiError,
      });
      if (!response?.ok) throw new Error(`Failed: ${response?.statusText}`);
      const result = await response.json();

      append(result);
      setReasons([...reasons, result]);
      setScreenReader(`Added ${result.reason}`);
      onComplete?.();
    } catch (error) {
      onApiError(getApiErrorMessages(error));
    } finally {
      setIsSubmitting(false);
      resetField('newReason');
    }
  };

  const onDelete = async (index: number) => {
    const reasonToDelete = fields[index];

    if (!reasonToDelete) {
      onApiError([errorMessages.generic]);
      return;
    }

    try {
      await deleteExclusionReason({ reason: reasonToDelete, onApiError });

      remove(index);
      setReasons(reasons.filter((r) => r.reason !== reasonToDelete.reason));
      setDeletingReasons((prev) =>
        prev.filter((r) => r !== reasonToDelete.reason)
      );
      setScreenReader(`Removed ${reasonToDelete.reason}`);
      onComplete?.();
    } catch {
      onApiError([`Error deleting ${reasonToDelete.reason}`]);
      setScreenReader(`Error deleting ${reasonToDelete.reason}`);
    } finally {
      onApiError(null);
    }
  };

  return (
    <form onSubmit={handleSubmit(onAdd)} className={styles.form}>
      <Stack spacing={3} className={styles.formGroup} component="fieldset">
        <Text
          variant="neutral"
          size="sm"
          component="legend"
          className={styles.formLegend}
        >
          {title}
        </Text>

        <Inline alignY="top" spacing={3}>
          <FormControl error={hasError}>
            <FormLabel isVisuallyHidden>{label}</FormLabel>
            <Input
              size="sm"
              aria-describedby={describedById}
              aria-invalid={!!hasError}
              aria-errormessage={hasError ? describedById : undefined}
              {...register('newReason', {
                validate: (value) => validateReason(value, fields),
              })}
            />
            <Text
              size="xs"
              variant="weak"
              id={describedById}
              className={classNames({
                [styles.visuallyHidden]: Boolean(hasError),
              })}
            />
          </FormControl>

          <Button
            htmlType="submit"
            isLoading={isSubmitting}
            type="brand"
            variant="outline"
            size="sm"
          >
            Add
          </Button>
        </Inline>
        <VisuallyHidden component="div" aria-live="polite" role="status">
          {screenReader}
        </VisuallyHidden>
        <ReorderItems
          items={fields}
          onMove={onMove}
          orderKey="uuid"
          className={styles.ReorderList}
        >
          <Stack spacing={1}>
            {fields.map((field, index) => (
              <ReorderItem
                key={`${index}.${field.uuid}`}
                id={field.uuid}
                dragHandleSize="sm"
              >
                <Inline
                  spacing={3}
                  alignX="left"
                  className={styles.ExclusionItem}
                >
                  <Text
                    size="sm"
                    variant="neutral"
                    className={styles.exclusionReason}
                  >
                    {field.reason}
                  </Text>
                  <Button
                    iconOnly
                    htmlType="button"
                    type="neutral"
                    variant="ghost"
                    size="sm"
                    isLoading={deletingReasons.includes(field.reason)}
                    onClick={() => onDelete(index)}
                    aria-label={`Delete ${field.reason} from exclusion reasons`}
                  >
                    <Icon size="md" icon={IconList.light.faTrashCan} />
                  </Button>
                </Inline>
              </ReorderItem>
            ))}
          </Stack>
        </ReorderItems>
      </Stack>
    </form>
  );
};

export default ExclusionForm;
